import React, { useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { Add, Remove } from '@mui/icons-material';
import { Select, MenuItem, FormControl } from '@mui/material';
import { styled } from '@mui/material/styles';

import { Order } from '../../models/Order';

type ReturnReason = 'damaged' | 'wrong_item' | 'not_as_described' | 'other';

interface ReturnItemData {
  orderItemId: string;
  quantity: number;
  reason: ReturnReason;
  description?: string;
}

interface ReturnFormData {
  items: ReturnItemData[];
  generalReason: ReturnReason;
  additionalNotes: string;
}

type ReturnOrderFormProps = {
  order: Order;
  onSubmit: (data: ReturnFormData) => Promise<void>;
  onCancel: () => void;
};

const returnReasons = [
  { value: 'damaged', label: 'Produkt uszkodzony' },
  { value: 'wrong_item', label: 'Otrzymano niewłaściwy produkt' },
  { value: 'not_as_described', label: 'Produkt niezgodny z opisem' },
  { value: 'other', label: 'Inny powód' },
];

const CustomSelect = styled(Select)({
  '& .MuiSelect-select': {
    padding: '8px 14px',
    borderRadius: '6px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#9CA3AF',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6366F1',
  },
});

const CustomButton = ({
  children,
  variant = 'primary',
  ...props
}: {
  children: React.ReactNode;
  variant?: 'primary' | 'outline';
  [key: string]: any;
}) => {
  const baseStyle =
    'px-6 py-2 rounded-md transition-all duration-200 font-medium text-sm w-full';
  const variants = {
    primary:
      'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800',
    outline:
      'border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100',
  };

  return (
    <motion.button
      className={`${baseStyle} ${variants[variant]}`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      {children}
    </motion.button>
  );
};

function ReturnOrderForm({ order, onSubmit, onCancel }: ReturnOrderFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const returnableItems = order.items.filter(
    (item) => item.quantity > item.returnedQuantity,
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ReturnFormData>({
    defaultValues: {
      items: returnableItems.map((item) => ({
        orderItemId: item.id,
        quantity: 0,
        reason: 'damaged',
        description: '',
      })),
      generalReason: 'damaged',
      additionalNotes: '',
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedItems = watch('items');
  const generalReason = watch('generalReason');

  const anyQuantitySelected = watchedItems?.some((item) => item.quantity > 0);

  const handleFormSubmit = async (data: ReturnFormData) => {
    if (!anyQuantitySelected) {
      toast.error('Wybierz co najmniej jeden produkt do zwrotu');
      return;
    }

    setIsSubmitting(true);

    try {
      const filteredData = {
        ...data,
        items: data.items.filter((item) => item.quantity > 0),
      };

      await onSubmit(filteredData);
    } catch (error) {
      console.error('Error submitting return form:', error);
      toast.error('Wystąpił błąd podczas wysyłania formularza zwrotu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyReasonToAll = () => {
    watchedItems.forEach((_, index) => {
      if (watchedItems[index].quantity > 0) {
        setValue(`items.${index}.reason`, generalReason as ReturnReason);
      }
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  if (returnableItems.length === 0) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <motion.div variants={itemVariants}>
          <div className="text-center py-6">
            <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Brak przedmiotów do zwrotu
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Wszystkie przedmioty z tego zamówienia zostały już zwrócone lub
              zamówienie nie kwalifikuje się do zwrotu.
            </p>
            <div className="mt-6">
              <CustomButton variant="primary" onClick={onCancel}>
                Powrót
              </CustomButton>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="bg-white rounded-lg shadow-sm p-6"
    >
      <motion.h2
        variants={itemVariants}
        className="text-xl font-bold mb-6 pb-2 border-b border-gray-200"
      >
        Formularz zwrotu zamówienia #{order.orderNumber}
      </motion.h2>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <motion.div variants={itemVariants} className="mb-6">
          <h3 className="text-lg font-medium mb-4">
            Wybierz produkty do zwrotu
          </h3>

          <div className="space-y-6 mb-6">
            {fields.map((field, index) => {
              const orderItem = order.items.find(
                (item) => item.id === field.orderItemId,
              );

              if (!orderItem) return null;

              const maxReturnQuantity =
                orderItem.quantity - orderItem.returnedQuantity;
              if (maxReturnQuantity <= 0) return null;

              return (
                <motion.div
                  key={field.id}
                  variants={itemVariants}
                  className={`border rounded-lg p-4 ${
                    watchedItems[index]?.quantity > 0
                      ? 'border-blue-200 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-20 h-20 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                      {orderItem.product?.mobilePrimaryImage ? (
                        <img
                          src={orderItem.product.mobilePrimaryImage}
                          alt={orderItem.productName}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                          No Image
                        </div>
                      )}
                    </div>

                    <div className="flex-grow">
                      <h4 className="font-medium text-gray-900">
                        {orderItem.productName}
                      </h4>
                      <p className="text-sm text-gray-500">
                        Cena:{' '}
                        {parseFloat(orderItem.productPrice as any).toFixed(2)}{' '}
                        zł
                      </p>
                      <div className="flex items-center mt-1 text-sm">
                        <span className="text-gray-700">
                          Dostępne do zwrotu: {maxReturnQuantity} z{' '}
                          {orderItem.quantity}
                        </span>
                        {orderItem.returnedQuantity > 0 && (
                          <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs">
                            Już zwrócono: {orderItem.returnedQuantity}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor={`quantity-${index}`}
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Ilość do zwrotu
                          </label>
                          <Controller
                            name={`items.${index}.quantity`}
                            control={control}
                            render={({ field }) => (
                              <motion.div
                                className="flex items-center gap-1 bg-gray-50 rounded-full px-2 py-1 w-fit"
                                whileHover={{ scale: 1.02 }}
                              >
                                <motion.button
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
                                  whileTap={{ scale: 0.9 }}
                                  type="button"
                                  onClick={() => {
                                    const newValue = Math.max(
                                      0,
                                      Number(field.value) - 1,
                                    );
                                    field.onChange(newValue);
                                  }}
                                >
                                  <Remove fontSize="small" />
                                </motion.button>
                                <span className="w-6 text-center font-medium text-sm">
                                  {field.value}
                                </span>
                                <motion.button
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
                                  whileTap={{ scale: 0.9 }}
                                  type="button"
                                  onClick={() => {
                                    const newValue = Math.min(
                                      maxReturnQuantity,
                                      Number(field.value) + 1,
                                    );
                                    field.onChange(newValue);
                                  }}
                                >
                                  <Add fontSize="small" />
                                </motion.button>
                              </motion.div>
                            )}
                          />
                        </div>

                        {watchedItems[index]?.quantity > 0 && (
                          <AnimatePresence>
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                            >
                              <label
                                htmlFor={`reason-${index}`}
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Powód zwrotu
                              </label>
                              <Controller
                                name={`items.${index}.reason`}
                                control={control}
                                render={({ field }) => (
                                  <FormControl fullWidth>
                                    <CustomSelect {...field}>
                                      {returnReasons.map((reason) => (
                                        <MenuItem
                                          key={reason.value}
                                          value={reason.value}
                                        >
                                          {reason.label}
                                        </MenuItem>
                                      ))}
                                    </CustomSelect>
                                  </FormControl>
                                )}
                              />
                            </motion.div>
                          </AnimatePresence>
                        )}
                      </div>

                      {watchedItems[index]?.quantity > 0 &&
                        watchedItems[index]?.reason === 'other' && (
                          <div className="mt-3">
                            <label
                              htmlFor={`description-${index}`}
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              Opisz powód zwrotu
                            </label>
                            <Controller
                              name={`items.${index}.description`}
                              control={control}
                              rules={{
                                required:
                                  'Opis jest wymagany przy wyborze "Inny powód"',
                              }}
                              render={({ field }) => (
                                <textarea
                                  id={`description-${index}`}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                  rows={2}
                                  placeholder="Opisz dlaczego chcesz zwrócić ten produkt..."
                                  {...field}
                                />
                              )}
                            />
                            {errors.items?.[index]?.description && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.items?.[index]?.description?.message}
                              </p>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="mb-6 border-t border-gray-200 pt-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-3">Powód zwrotu</h3>
              <p className="text-sm text-gray-500 mb-3">
                Możesz ustawić jednakowy powód zwrotu dla wszystkich wybranych
                produktów
              </p>

              <div className="flex flex-col items-center space-y-4">
                <Controller
                  name="generalReason"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <CustomSelect {...field}>
                        {returnReasons.map((reason) => (
                          <MenuItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </MenuItem>
                        ))}
                      </CustomSelect>
                    </FormControl>
                  )}
                />
                <CustomButton
                  variant="outline"
                  onClick={applyReasonToAll}
                  disabled={!anyQuantitySelected}
                >
                  Zastosuj do wszystkich
                </CustomButton>
              </div>
            </div>

            <div>
              <label
                htmlFor="additionalNotes"
                className="block text-lg font-medium mb-3"
              >
                Dodatkowe uwagi
              </label>
              <Controller
                name="additionalNotes"
                control={control}
                render={({ field }) => (
                  <textarea
                    id="additionalNotes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    rows={4}
                    placeholder="Wpisz dodatkowe informacje istotne dla procesu zwrotu..."
                    {...field}
                  />
                )}
              />
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex justify-between pt-4 border-t border-gray-200 space-x-4"
        >
          <CustomButton variant="outline" onClick={onCancel}>
            Anuluj
          </CustomButton>

          <CustomButton
            type="submit"
            disabled={isSubmitting || !anyQuantitySelected}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-4">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Przetwarzanie...
              </div>
            ) : (
              'Złóż wniosek o zwrot'
            )}
          </CustomButton>
        </motion.div>
      </form>
    </motion.div>
  );
}

export default ReturnOrderForm;
